import { createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { GuideCardGrid, GuideFooter, GuideSteps } from "@/components/guide/GuideSections";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Resources", path: "/resources" },
  { name: "How To Check Domain Authority", path: "/how-to-check-domain-authority" },
];

const TOOLS = [
  {
    t: "Moz Link Explorer / MozBar",
    m: "Domain Authority (DA)",
    free: "10 queries a month on a free account; MozBar shows DA on any page you browse.",
    d: "The original score, and the one clients usually mean when they say \"DA\". Refreshes roughly monthly.",
  },
  {
    t: "Semrush",
    m: "Authority Score (AS)",
    free: "10 lookups a day on the free tier through Domain Overview.",
    d: "Mixes link quality, organic traffic and spam signals, so it drops when a profile looks bought. The one I quote in reports.",
  },
  {
    t: "Ahrefs Website Authority Checker",
    m: "Domain Rating (DR)",
    free: "Unlimited basic checks on the free tool; full backlink detail needs a paid plan.",
    d: "Pure link maths. Fast to read, easiest to inflate, so treat a high DR with a low traffic number as a red flag.",
  },
  {
    t: "Majestic",
    m: "Trust Flow / Citation Flow",
    free: "Limited free checks; the browser plugin covers casual use.",
    d: "Useful as a second opinion — a Citation Flow far above Trust Flow usually means volume without quality.",
  },
];

const STEPS = [
  {
    t: "Pick one tool and make it your source of truth",
    d: "DA, AS and DR are not interchangeable. Mixing them across months makes the trend meaningless. Choose the one your reporting stack already uses and record the others as secondary.",
  },
  {
    t: "Enter the root domain, not a page URL",
    d: "Type example.com, not https://example.com/blog/post. A page URL returns page-level scores (Page Authority, URL Rating), which answer a different question.",
  },
  {
    t: "Check both www and non-www once",
    d: "If a redirect is misconfigured, the two versions can hold separate link profiles and separate scores. Finding that on the first check has saved several clients months of guesswork.",
  },
  {
    t: "Record the number with a date",
    d: "Scores refresh on the vendor's schedule, not yours. Without a dated log you cannot tell a real climb from an index refresh.",
  },
  {
    t: "Benchmark against the sites actually ranking",
    d: "Check the top five results for your main keyword the same day. Your score only matters relative to the competitors in that specific SERP.",
  },
];

const MISTAKES = [
  { t: "Comparing DA from one month to DR from the next", d: "Different vendors, different indexes, different curves. The apparent 12-point jump is a change of tool, not progress." },
  { t: "Checking weekly", d: "Moz refreshes DA roughly monthly, Ahrefs updates DR far more often. Weekly checks mostly measure index noise. Quarterly is enough for reporting." },
  { t: "Trusting free 'DA checker' sites", d: "Most scrape or estimate, and some inflate to sell links. Use the vendors' own tools — Moz, Semrush and Ahrefs all allow free checks." },
  { t: "Reading the score as a Google metric", d: "It isn't one. It is a third-party estimate, useful for benchmarking and for spotting a link profile going wrong." },
];

const FAQS = [
  {
    q: "How do I check domain authority?",
    a: "Open Moz Link Explorer, Semrush Domain Overview or the Ahrefs Website Authority Checker, enter your root domain (example.com, not a full page URL) and read the score — Moz reports Domain Authority, Semrush reports Authority Score and Ahrefs reports Domain Rating. All three allow free checks, and all three should be recorded with the date you checked.",
  },
  {
    q: "How can I check domain authority for free?",
    a: "Ahrefs' Website Authority Checker is free and unlimited for basic DR lookups. Moz gives 10 free Link Explorer queries a month with an account, plus the MozBar browser extension. Semrush allows 10 free Domain Overview lookups a day. Between the three you can check any domain without paying.",
  },
  {
    q: "Which domain authority checker is most accurate?",
    a: "None is objectively accurate, because none is a Google metric. Semrush Authority Score correlates best with real organic performance because it factors in traffic and spam signals; Ahrefs DR has the largest link index; Moz DA is the most widely quoted. Pick one, stay with it, and compare against competitors rather than chasing an absolute number.",
  },
  {
    q: "Why is my domain authority different in each tool?",
    a: "Each vendor crawls its own index and runs its own model. Ahrefs may see backlinks Moz has not crawled, and Semrush deliberately penalises profiles that look purchased. A 10–20 point spread between DA, DR and AS for the same site is normal and not a sign anything is broken.",
  },
  {
    q: "How often should I check my domain authority?",
    a: "Monthly at most, quarterly for reporting. Moz refreshes DA on roughly a monthly cycle, so checking more often measures index noise rather than progress. Track rankings, traffic and referring domains weekly instead — those move first, and authority scores follow.",
  },
  {
    q: "How do I check page authority instead of domain authority?",
    a: "Paste the full page URL rather than the root domain. Moz then returns Page Authority and Ahrefs returns URL Rating, both estimating the strength of that single page. Use these when deciding which page to link from internally or which URL to build links to.",
  },
];

export const Route = createFileRoute("/how-to-check-domain-authority")({
  head: () =>
    buildSeo({
      title: "How To Check Domain Authority (Free Tools, 2026)",
      description:
        "Where to check your domain authority for free, which score each tool reports, and the five-step routine for reading DA, Authority Score and DR without fooling yourself.",
      path: "/how-to-check-domain-authority",
      ogEyebrow: "Guide",
      keywords:
        "how to check domain authority, domain authority checker, free domain authority check, check DA of website, Moz DA checker, Ahrefs DR checker, Semrush authority score",
    }),
  component: CheckDomainAuthorityPage,
});

function CheckDomainAuthorityPage() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />

      <Section headingLevel={1}
        eyebrow="Guide"
        title={<>How to check <span className="text-gradient">domain authority</span></>}
        subtitle="Enter your root domain in Moz Link Explorer, Semrush Domain Overview or the Ahrefs Website Authority Checker. Each returns its own score — Domain Authority, Authority Score and Domain Rating respectively. All three offer free checks; pick one as your baseline and record it with a date."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "Moz calls it DA, Semrush calls it Authority Score, Ahrefs calls it DR — they are different models and are not comparable to each other.",
              "Free checks exist in all three tools; you do not need a paid plan to baseline a site.",
              "Enter the root domain for site-level scores; enter a full URL for page-level ones.",
              "Check monthly at most — Moz refreshes roughly monthly, so weekly checks just measure noise.",
              "A score only means something next to the competitors currently ranking for your keyword.",
            ]}
          />
          <ReviewedBy
            reviewedOn="2026-08-18"
            credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh"
          />
        </div>
      </Section>

      <Section eyebrow="Tools" title="Where to check, and what each one reports">
        <div className="grid md:grid-cols-2 gap-6 -mt-4">
          {TOOLS.map((c, i) => (
            <Reveal key={c.t} delay={(i % 2) * 0.06}>
              <div className="rounded-3xl border border-border bg-card p-7 h-full">
                <h3 className="font-display text-lg">{c.t}</h3>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-primary/80">{c.m}</p>
                <p className="mt-3 text-sm text-muted-foreground">{c.d}</p>
                <p className="mt-3 text-sm text-muted-foreground/80"><strong className="text-foreground/80">Free access:</strong> {c.free}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section eyebrow="Method" title="A five-step check that gives you a usable number">
        <GuideSteps steps={STEPS} anchors />
      </Section>

      <Section eyebrow="Pitfalls" title="Four ways people misread the number">
        <GuideCardGrid items={MISTAKES} />
      </Section>

      <GuideFooter
        nextTitle="Related reading"
        links={[
          { label: "How to improve domain authority", to: "/how-to-improve-domain-authority" },
          { label: "Moz DA vs Ahrefs DR", to: "/moz-da-vs-ahrefs-dr" },
          { label: "SEO glossary", to: "/glossary" },
          { label: "Free SEO audit", to: "/free-seo-audit" },
        ]}
        aiContext="checking website authority"
        aiQuestions={["measure-aeo-citations", "ai-search-traffic-analytics", "what-is-entity-seo", "core-web-vitals-2026"]}
        faqs={FAQS}
        faqIdPrefix="faq-check-domain-authority"
        ctaTitle={<>Want someone to read the <span className="text-gradient">whole profile</span>, not just the score?</>}
      />

      <JsonLd data={faqSchema(FAQS)} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to check domain authority",
          description:
            "A five-step routine for checking a website's domain authority in Moz, Semrush or Ahrefs and recording a number you can actually trend.",
          totalTime: "PT10M",
          step: STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.t,
            text: s.d,
            url: `${SITE.url}/how-to-check-domain-authority#step-${i + 1}`,
          })),
        }}
      />
      <JsonLd data={breadcrumbSchema(CRUMBS)} />
    </>
  );
}
